'use client';

import { createContext } from 'react';
import { Aperture, Kanban, Monitor, Moon, Plus, Sun } from '@/components/icons';
import type { ThemePref } from '@/components/ui/use-theme';
import { m, type Message, type Translator } from '@/i18n';
import { fuzzyRank, type Ranked } from '@/lib/fuzzy';
import type { SwitcherTarget } from './quick-switcher';

/**
 * The command palette's registry (Ctrl+P / the `>` prefix in quick-open): a
 * flat, client-side list of typed commands the shell assembles. V1 is global
 * commands only - navigation, create, theme; context-sensitive commands (an
 * open item's state-machine verbs) plug in later through
 * `PaletteCommandsContext` (a route surface provides its commands, the palette
 * consumes the union - the seam exists, the board item "Global search /
 * criteria search: deferrals" has the entry).
 */
export interface CommandCtx {
  /** Navigate (the switcher's router.push; a jump, clears the trail). */
  push: (href: string) => void;
  /** Close the palette (a command that opens its own surface calls this first). */
  close: () => void;
}

export interface PaletteCommand {
  id: string;
  /** What the row says, as a descriptor the palette formats in the page's
   *  locale (docs/I18N.md); the query is ranked against that text. */
  label: Message;
  /** Extra match words beyond the label ("night" for the dark theme), as a
   *  descriptor of space-separated words in the page's locale. */
  keywords?: Message;
  icon?: React.ReactNode;
  run: (ctx: CommandCtx) => void;
}

/** Context-sensitive commands, contributed by the current route's surface.
 *  Deliberately empty in v1 - the extension seam, not dead code. */
export const PaletteCommandsContext = createContext<PaletteCommand[]>([]);

/**
 * The global command set. Navigation derives from the switcher's page targets
 * (one vocabulary, no second list to drift); create and theme close over the
 * shell's handlers.
 */
export function buildGlobalCommands(opts: {
  navTargets: SwitcherTarget[];
  onNewItem: () => void;
  onNewLens: () => void;
  onNewView: () => void;
  setTheme: (pref: ThemePref) => void;
}): PaletteCommand[] {
  const goTo = opts.navTargets
    .filter((t) => t.kind === 'page')
    .map(
      (t): PaletteCommand => ({
        id: `go:${t.href}`,
        label: m('palette.command.goTo', { page: t.label }),
        keywords: m('palette.keywords.goTo'),
        icon: t.icon,
        run: (ctx) => ctx.push(t.href),
      }),
    );
  return [
    ...goTo,
    {
      id: 'new-item',
      label: m('palette.command.newItem'),
      keywords: m('palette.keywords.newItem'),
      icon: <Plus aria-hidden />,
      run: (ctx) => {
        ctx.close();
        opts.onNewItem();
      },
    },
    {
      id: 'new-lens',
      label: m('palette.command.newLens'),
      keywords: m('palette.keywords.newLens'),
      icon: <Aperture aria-hidden />,
      run: (ctx) => {
        ctx.close();
        opts.onNewLens();
      },
    },
    {
      id: 'new-view',
      label: m('palette.command.newView'),
      keywords: m('palette.keywords.newView'),
      icon: <Kanban aria-hidden />,
      run: (ctx) => {
        ctx.close();
        opts.onNewView();
      },
    },
    {
      id: 'theme-light',
      label: m('palette.command.themeLight'),
      keywords: m('palette.keywords.themeLight'),
      icon: <Sun aria-hidden />,
      run: (ctx) => {
        opts.setTheme('light');
        ctx.close();
      },
    },
    {
      id: 'theme-dark',
      label: m('palette.command.themeDark'),
      keywords: m('palette.keywords.themeDark'),
      icon: <Moon aria-hidden />,
      run: (ctx) => {
        opts.setTheme('dark');
        ctx.close();
      },
    },
    {
      id: 'theme-system',
      label: m('palette.command.themeSystem'),
      keywords: m('palette.keywords.themeSystem'),
      icon: <Monitor aria-hidden />,
      run: (ctx) => {
        opts.setTheme('system');
        ctx.close();
      },
    },
  ];
}

/** Rank commands for the palette's query: the shared fuzzy engine over the
 *  label, as `t` formats it, plus its match keywords (an empty query lists
 *  them all, in order). */
export function filterCommands(
  q: string,
  commands: PaletteCommand[],
  t: Translator,
): Ranked<PaletteCommand>[] {
  return fuzzyRank(q, commands, (c) =>
    [t.msg(c.label), ...(c.keywords ? [t.msg(c.keywords)] : [])].join(' '),
  );
}
